'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useFundWallet, useLoginWithEmail, usePrivy, useWallets } from '@privy-io/react-auth';

import { useAuth } from '@/hooks/useAuth';
import { useMixpanel } from '@/hooks/useMixpanel';
import { useLinkWallet } from '@/services/query';
import { useAppDispatch } from '@/store';
import { updateUser } from '@/store/slices/auth.slice';

import { MESSAGES } from '@/constants';

export function usePrivyWallet() {
  const { identifyUser, trackWalletConnected } = useMixpanel();
  const { login, logout, ready, authenticated, user: privyUser, createWallet } = usePrivy();
  const { wallets } = useWallets();
  const { fundWallet } = useFundWallet();
  const { sendCode, loginWithCode } = useLoginWithEmail();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);

  const { user: authUser, isAuthenticated, accessToken } = useAuth();
  const dispatch = useAppDispatch();
  const linkWalletMutation = useLinkWallet();
  const hasLinkedWalletRef = useRef(false);
  const isCreatingWalletRef = useRef(false);
  const normalizedAuthWallet = authUser?.walletAddress?.toLowerCase();
  const linkedWallet = wallets.find((w) => w.address?.toLowerCase() === normalizedAuthWallet);
  const embeddedWallet = wallets.find((w) => w.walletClientType === 'privy');
  // SECURITY: never fall back to wallets[0] — that could be a MetaMask/external
  // wallet still connected at the browser level from a previous user's session.
  // Only trust the embedded wallet (always belongs to this Privy user) or the
  // wallet matching the backend's stored walletAddress (proven linked to this user).
  const activeWallet = linkedWallet || embeddedWallet || null;
  const activeWalletAddress = activeWallet?.address || privyUser?.wallet?.address;
  const activeWalletType =
    activeWallet?.walletClientType === 'privy' ? 'privy_embedded' : 'external';

  const link = useCallback(() => {
    if (authenticated && isAuthenticated && accessToken && privyUser?.id && activeWalletAddress) {
      linkWalletMutation.mutate(
        {
          privyUserId: privyUser.id,
          eoaAddress: activeWalletAddress,
          walletType: activeWalletType,
        },
        {
          onSuccess: (response) => {
            dispatch(updateUser({ walletAddress: response.data.eoaAddress }));
            identifyUser(
              response.data.eoaAddress,
              privyUser?.wallet?.walletClientType || 'privy_embedded',
            );
            trackWalletConnected({
              wallet_type: privyUser?.wallet?.walletClientType || 'privy_embedded',
              chain_id: privyUser?.wallet?.chainId || 'unknown',
            });
          },
          onError: (error) => {
            console.error('[usePrivyWallet] linkWallet failed:', error);
          },
        },
      );
      hasLinkedWalletRef.current = true;
    }
  }, [
    authenticated,
    isAuthenticated,
    accessToken,
    privyUser?.id,
    activeWalletAddress,
    activeWalletType,
    linkWalletMutation,
    dispatch,
  ]);

  useEffect(() => {
    if (
      authenticated &&
      isAuthenticated &&
      accessToken &&
      privyUser?.id &&
      activeWalletAddress &&
      !hasLinkedWalletRef.current
    ) {
      link();
    }
  }, [authenticated, isAuthenticated, accessToken, privyUser?.id, activeWalletAddress, link]);

  const sendOtp = useCallback(
    async (email: string) => {
      try {
        setIsAuthenticating(true);
        await sendCode({ email });
        setIsOtpSent(true);
        return true;
      } catch (error) {
        console.error('Failed to send OTP:', error);
        toast.error(MESSAGES.AUTH.OTP_SEND_FAILED);
        return false;
      } finally {
        setIsAuthenticating(false);
      }
    },
    [sendCode],
  );

  const submitOtp = useCallback(
    async (code: string) => {
      try {
        setIsAuthenticating(true);
        await loginWithCode({ code });
        return true;
      } catch (error) {
        console.error('Failed to login with OTP:', error);
        toast.error(MESSAGES.AUTH.INVALID_OTP);
        return false;
      } finally {
        setIsAuthenticating(false);
      }
    },
    [loginWithCode],
  );

  const initWallet = useCallback(async () => {
    if (!ready) return false;

    // Reset creation guard on new initialization attempt
    isCreatingWalletRef.current = false;
    hasLinkedWalletRef.current = false;

    if (authUser?.email) {
      return await sendOtp(authUser.email);
    }

    console.warn('[usePrivyWallet] Cannot init wallet: authUser email missing', { authUser });
    return false;
  }, [ready, authUser?.email, sendOtp]);

  return {
    initWallet,
    logout,
    sendOtp,
    submitOtp,
    fund: fundWallet,
    isAuthenticating,
    isOtpSent,
    ready,
    authenticated,
    user: privyUser,
    wallet: activeWallet || privyUser?.wallet,
    link: () => {
      hasLinkedWalletRef.current = false;
      link();
    },
    linkStatus: {
      isLinking: linkWalletMutation.isPending,
      isError: linkWalletMutation.isError,
      error: linkWalletMutation.error,
    },
  };
}

export default usePrivyWallet;
